import { createTheme, PaletteColorOptions, Theme } from '@mui/material/styles';
import { red } from '@mui/material/colors';
import { deDE } from '@mui/material/locale';
import { colors } from '@mui/material';

declare module '@mui/material/styles' {
  interface Palette {
    disabled: PaletteColorOptions;
  }
  interface PaletteOptions {
    disabled: PaletteColorOptions;
  }
}

export const seminargoColor = '#ac2a6e';
export const semColor = '#266670';

/**
 * Creates a MUI theme with custom primary color and German locale.
 * Applies consistent styling overrides for icons, inputs, tables, and other components.
 *
 * @param primaryColor - Hex color code for the primary theme color
 * @returns Configured MUI theme
 */
export const createSeminargoTheme = (primaryColor: string): Theme => {
  return createTheme(
    {
      components: {
        MuiIconButton: {
          defaultProps: {
            size: 'small',
          },
          styleOverrides: {
            root: {
              '& svg': {
                fontSize: '1.25rem',
              },
            },
          },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: {
              padding: '6px 16px',
            },
          },
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              '& svg': {
                height: '0.8em',
                width: '0.8em',
              },
            },
          },
        },
        MuiRadio: {
          styleOverrides: {
            root: {
              '& svg': {
                height: '0.8em',
                width: '0.8em',
              },
            },
          },
        },
        MuiInputAdornment: {
          styleOverrides: {
            root: {
              marginLeft: '10px',
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            input: {
              fontSize: '0.875rem',
              padding: '12px 12px',
            },
            root: {
              padding: '0',
            },
          },
        },
        MuiInputLabel: {
          styleOverrides: {
            outlined: {
              fontSize: '0.805rem',
              marginTop: '-2px',
            },
            shrink: {
              marginTop: '1px !important',
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontSize: '0.875rem',
              minHeight: 'auto',
              padding: '12px',
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              minHeight: 'auto',
            },
          },
        },
        MuiTableContainer: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              border: '1px solid #eee',
              '& a': {
                color: primaryColor,
                textDecoration: 'none',
                '&:hover': {
                  textDecoration: 'underline',
                },
              },
              '& .MuiButton-text': {
                color: primaryColor,
                '&:hover': {
                  textDecoration: 'underline',
                  backgroundColor: 'transparent',
                },
              },
            },
          },
        },
        MuiAccordion: {
          styleOverrides: {
            root: {
              border: '1px solid #e0e0e0',
              boxShadow: 'none',
            },
          },
        },
        MuiListSubheader: {
          styleOverrides: {
            root: {
              fontWeight: 'bold',
              paddingLeft: '16px',
            },
          },
        },
        MuiListItemIcon: {
          styleOverrides: {
            root: {
              minWidth: '36px',
              '& svg': {
                fontSize: '1.25rem',
              },
            },
          },
        },
        MuiListItemText: {
          styleOverrides: {
            primary: {
              fontSize: '0.875rem',
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              paddingLeft: '24px',
              transition: 'all 0.2s ease-in-out',
            },
          },
        },
        MuiMenu: {
          styleOverrides: {
            paper: {
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            },
          },
        },
        MuiTableCell: {
          styleOverrides: {
            root: {
              padding: '12px',
              fontSize: '0.875rem',
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              boxShadow: 'none',
              fontSize: '0.875rem',
              ':hover': {
                boxShadow: 'none',
              },
            },
            containedSecondary: {
              border: '1px solid ' + primaryColor,
              color: primaryColor,
              '&:disabled': {
                border: '1px solid #999',
              },
              '&:hover': {
                backgroundColor: primaryColor + '11',
              },
            },
          },
        },
        MuiLink: {
          styleOverrides: {
            root: {
              color: primaryColor,
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            },
          },
        },
      },
      palette: {
        primary: {
          main: primaryColor,
        },
        secondary: {
          main: '#fff',
        },
        disabled: {
          main: '#999',
        },
        error: {
          main: red.A400,
        },
      },
    },
    deDE,
  );
};

// Default theme with Seminargo color
const theme = createSeminargoTheme(seminargoColor);

export default theme;
